import { useState } from 'react';
import { Stack } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { AdminAuditLogFilters } from '@/api/models';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { countActiveAuditLogFilters } from './filters/activeFilterCount';
import { AuditLogFiltersForm } from './filters/AuditLogFiltersForm';
import { AuditLogListView } from './AuditLogListView';

const DEFAULT_FILTERS: AdminAuditLogFilters = { page: 1, pageSize: 50 };
const PAGINATION_ONLY_KEYS = new Set(['page', 'pageSize']);

function downloadCsvFilename(): string {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  return `auditoria-${stamp}.csv`;
}

/**
 * Audit console: `GET /v1/admin/audit-log` with the contract's filters and
 * pagination under one sticky `SectionHeader`/`FiltersPopover`, and CSV
 * export against `GET /v1/admin/audit-log/export` (D-M / A3). Read-only for
 * every role — `platform:auditor` has full read access here, including
 * export, so there is no write guard and no `useCanWrite` check anywhere on
 * this screen, unlike every other administration surface.
 */
export function AuditPage() {
  const translate = useTranslate();
  const notify = useNotify();
  const [filters, setFilters] = useState<AdminAuditLogFilters>(DEFAULT_FILTERS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [exporting, setExporting] = useState(false);

  function updateFilters(patch: Partial<AdminAuditLogFilters>) {
    const isPaginationOnly = Object.keys(patch).every((key) => PAGINATION_ONLY_KEYS.has(key));
    setFilters((prev) => ({ ...prev, ...patch, page: isPaginationOnly ? (patch.page ?? prev.page) : 1 }));
  }

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await payApiClient.exportAdminAuditLog(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadCsvFilename();
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify(translate('pay.audit.list.exportStarted'), { type: 'info' });
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : translate('pay.audit.list.exportError');
      notify(message, { type: 'error' });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={<PageTitle title={translate('pay.audit.pageTitle')} subtitle={translate('pay.audit.pageSubtitle')} />}
        filters={
          <FiltersPopover activeCount={countActiveAuditLogFilters(filters)} onClear={() => setFilters(DEFAULT_FILTERS)}>
            <AuditLogFiltersForm filters={filters} onChange={updateFilters} />
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onExport={exporting ? undefined : handleExport}
      />
      <AuditLogListView key={refreshToken} filters={filters} onFiltersChange={updateFilters} />
    </Stack>
  );
}
